const pageVisibilitySubscribers = new Set()
const motionPreferenceSubscribers = new Set()
const mediaVisibilityCallbacks = new Map()
const mediaVisibilityRatios = new Map()

let pageVisibilityListening = false
let motionPreferenceListening = false
let motionPreferenceQuery = null
let mediaObserver = null

function notifyPageVisibility() {
  const isVisible = document.visibilityState !== 'hidden'
  pageVisibilitySubscribers.forEach((subscriber) => subscriber(isVisible))
}

function subscribePageVisibility(subscriber) {
  if (typeof document === 'undefined') return () => {}

  pageVisibilitySubscribers.add(subscriber)

  if (!pageVisibilityListening) {
    document.addEventListener('visibilitychange', notifyPageVisibility)
    pageVisibilityListening = true
  }

  subscriber(document.visibilityState !== 'hidden')

  return () => {
    pageVisibilitySubscribers.delete(subscriber)

    if (pageVisibilityListening && pageVisibilitySubscribers.size === 0) {
      document.removeEventListener('visibilitychange', notifyPageVisibility)
      pageVisibilityListening = false
    }
  }
}

function notifyMotionPreference(event) {
  motionPreferenceSubscribers.forEach((subscriber) => subscriber(event.matches))
}

export function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function subscribeMotionPreference(subscriber) {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {}

  motionPreferenceQuery =
    motionPreferenceQuery || window.matchMedia('(prefers-reduced-motion: reduce)')
  motionPreferenceSubscribers.add(subscriber)

  if (!motionPreferenceListening) {
    if (motionPreferenceQuery.addEventListener) {
      motionPreferenceQuery.addEventListener('change', notifyMotionPreference)
    } else {
      motionPreferenceQuery.addListener(notifyMotionPreference)
    }
    motionPreferenceListening = true
  }

  subscriber(motionPreferenceQuery.matches)

  return () => {
    motionPreferenceSubscribers.delete(subscriber)

    if (motionPreferenceListening && motionPreferenceSubscribers.size === 0) {
      if (motionPreferenceQuery.removeEventListener) {
        motionPreferenceQuery.removeEventListener('change', notifyMotionPreference)
      } else {
        motionPreferenceQuery.removeListener(notifyMotionPreference)
      }
      motionPreferenceListening = false
    }
  }
}

function ensureMediaObserver() {
  if (mediaObserver || typeof IntersectionObserver === 'undefined') return

  mediaObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        mediaVisibilityRatios.set(
          entry.target,
          entry.isIntersecting ? entry.intersectionRatio : 0,
        )
      })

      let activeMedia = null
      let activeRatio = 0.28

      mediaVisibilityRatios.forEach((ratio, element) => {
        if (ratio > activeRatio) {
          activeMedia = element
          activeRatio = ratio
        }
      })

      mediaVisibilityCallbacks.forEach((callback, element) => {
        callback(element === activeMedia)
      })
    },
    {
      rootMargin: '8% 0px',
      threshold: [0, 0.28, 0.55],
    },
  )
}

function observeMediaVisibility(element, callback) {
  if (typeof IntersectionObserver === 'undefined') {
    callback(true)
    return () => {}
  }

  ensureMediaObserver()
  mediaVisibilityCallbacks.set(element, callback)
  mediaVisibilityRatios.set(element, 0)
  mediaObserver.observe(element)

  return () => {
    mediaObserver.unobserve(element)
    mediaVisibilityCallbacks.delete(element)
    mediaVisibilityRatios.delete(element)

    if (mediaVisibilityCallbacks.size === 0) {
      mediaObserver.disconnect()
      mediaObserver = null
    }
  }
}

export function bindMotionMedia(video) {
  let isNearViewport = false
  let isPageVisible = true
  let isReduced = prefersReducedMotion()
  const savesData =
    typeof navigator !== 'undefined' &&
    navigator.connection &&
    navigator.connection.saveData
  let destroyed = false

  const syncPlayback = () => {
    if (destroyed) return

    const shouldPlay = isNearViewport && isPageVisible && !isReduced && !savesData

    if (shouldPlay) {
      const playPromise = video.play()
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {})
      }
    } else {
      video.pause()
    }
  }

  const stopObservingMedia = observeMediaVisibility(video, (isVisible) => {
    isNearViewport = isVisible
    syncPlayback()
  })
  const stopObservingPage = subscribePageVisibility((isVisible) => {
    isPageVisible = isVisible
    syncPlayback()
  })
  const stopObservingMotion = subscribeMotionPreference((isReducedMotion) => {
    isReduced = isReducedMotion
    syncPlayback()
  })

  return () => {
    destroyed = true
    video.pause()
    stopObservingMedia()
    stopObservingPage()
    stopObservingMotion()
  }
}
